import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { useDeleteWorkspaceMutation } from '../../hooks/useMutations/useWorkspacesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'
import { LoaderButton } from '../LoaderButton'

export const DeleteWorkspaceModal = NiceModal.create(
  ({ companyId, workspaceId }: { companyId: string; workspaceId: string }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const [confirmationText, setConfirmationText] = useState('')
    const { showSnackbar } = useSnackbar()
    const deleteWorkspaceMutation = useDeleteWorkspaceMutation(
      companyId,
      workspaceId
    )
    const navigate = useNavigate()

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '32.5625rem',
            padding: '1.5rem',
            borderRadius: '.25rem'
          }
        }}
      >
        <DialogTitle sx={{ padding: '1rem 1.5rem' }}>
          <Typography variant='h6' component='span'>
            {t('delete_workspace_modal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem' }}>
          <Stack direction='column' spacing='1rem'>
            <Typography variant='body1'>
              <Trans
                i18nKey='delete_workspace_modal.description'
                components={[
                  <MuiLink
                    component={Link}
                    to='/terms'
                    target='_blank'
                    rel='noopener noreferrer'
                    underline='hover'
                    display='inline'
                  />
                ]}
              />
            </Typography>
            <Typography variant='body1'>
              {t('delete_workspace_modal.confirmation')}
            </Typography>
          </Stack>
          <TextField
            fullWidth
            sx={{ py: '1rem' }}
            value={confirmationText}
            onChange={e => setConfirmationText(e.target.value)}
          ></TextField>
        </DialogContent>
        <DialogActions sx={{ padding: '.5rem' }}>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => modal.remove()}
            sx={{
              textTransform: 'none',
              width: '14rem',
              padding: '.5rem 1.375rem',

              backgroundColor: 'rgba(178, 223, 219, 0.10)'
            }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            label={t('delete_workspace_modal.button')}
            loading={deleteWorkspaceMutation.isPending}
            variant='contained'
            color='error'
            sx={{
              textTransform: 'none',
              width: '14rem',
              padding: '.5rem 1.375rem'
            }}
            disabled={
              !confirmationText ||
              confirmationText !==
                t('delete_workspace_modal.confirmation_text') ||
              deleteWorkspaceMutation.isPending
            }
            onClick={() =>
              deleteWorkspaceMutation.mutate(undefined, {
                onSuccess: () => {
                  showSnackbar({
                    message: t('delete_workspace_modal.success'),
                    severity: 'success'
                  })
                  navigate(`/companies/${companyId}/workspaces`)
                },
                onError: () => {
                  showSnackbar({
                    message: t('delete_workspace_modal.error'),
                    severity: 'error'
                  })
                },
                onSettled: () => {
                  setConfirmationText('')
                  modal.remove()
                }
              })
            }
          />
        </DialogActions>
      </Dialog>
    )
  }
)
